import type { FeaturesInputBodyFeaturesItem } from '@/shared/api/generated/model';
import {
  siteFeaturesSet,
  sitesEnvironments,
  sitesSetEnvironmentAddress,
  sitesUpdate,
} from '@/shared/api/generated/sites/sites';
import { ApiError } from '@/shared/api/http';

export type SiteFeature = FeaturesInputBodyFeaturesItem;

/**
 * Возможности сайта (docs/research/13-site-creation.md в faceta-back): одни и
 * те же карточки — при создании сайта и в «Настройках → Возможности».
 */
export const SITE_FEATURES: { key: SiteFeature; title: string; text: string }[] = [
  {
    key: 'users',
    title: 'Вход посетителей',
    text: 'Регистрация, вход и личный кабинет на сайте. В меню — «Пользователи».',
  },
  {
    key: 'shop',
    title: 'Магазин',
    text: 'Каталог, корзина, заказы и оплата. В меню — «Магазин».',
  },
  {
    key: 'forms',
    title: 'Формы и заявки',
    text: 'Обратная связь и заявки с сайта — в админке, без своего бэкенда. В меню — «Формы».',
  },
];

/** Порядок возможностей — как в карточках. */
export function orderedFeatures(features: readonly string[]): SiteFeature[] {
  return SITE_FEATURES.map((f) => f.key).filter((key) => features.includes(key));
}

/**
 * Языки из строки «ru, en»: нижний регистр у языка, верхний — у региона
 * (pt-BR), без повторов и пустых. Пусто — русский.
 */
export function parseLanguages(raw: string): string[] {
  const out: string[] = [];

  for (const part of raw.split(/[\s,;]+/)) {
    const [lang = '', region] = part.trim().split('-');
    const code =
      region === undefined ? lang.toLowerCase() : `${lang.toLowerCase()}-${region.toUpperCase()}`;

    if (code !== '' && !out.includes(code)) {
      out.push(code);
    }
  }

  return out.length > 0 ? out : ['ru'];
}

export interface SiteSetup {
  features: SiteFeature[];
  languages: string[];
  developmentURL: string;
  productionURL: string;
}

/**
 * Доводит только что созданный сайт: возможности, языки, адреса окружений.
 * Шаги независимы — не прошёл один, остальные всё равно делаются; сайт уже
 * создан, и недоделанное можно поправить в настройках. Отдаёт, что не вышло
 * и почему.
 */
export async function setupSite(siteId: string, etag: string, setup: SiteSetup): Promise<string[]> {
  const failed: string[] = [];
  const reason = (cause: unknown) =>
    cause instanceof ApiError ? cause.detail : 'нет связи с сервером';

  // Языки — первыми: правка возможностей меняет версию сайта, и с версией
  // от создания языки после неё не сохранились бы.
  if (setup.languages.join(',') !== 'ru') {
    await sitesUpdate(siteId, { languages: setup.languages }, { 'If-Match': etag }).catch(
      (cause: unknown) => {
        failed.push(`языки — ${reason(cause)}`);
      },
    );
  }

  if (setup.features.length > 0) {
    await siteFeaturesSet(siteId, { features: setup.features }).catch((cause: unknown) => {
      failed.push(`возможности — ${reason(cause)}`);
    });
  }

  const urls = { development: setup.developmentURL.trim(), production: setup.productionURL.trim() };

  if (urls.development !== '' || urls.production !== '') {
    try {
      const response = await sitesEnvironments(siteId);
      const envs = response.status === 200 ? (response.data.items ?? []) : [];

      for (const kind of ['development', 'production'] as const) {
        const env = envs.find((item) => item.kind === kind);

        if (urls[kind] === '' || env === undefined) {
          continue;
        }

        await sitesSetEnvironmentAddress(
          siteId,
          kind,
          { url: urls[kind], extra_origins: [] },
          { 'If-Match': env.etag },
        ).catch((cause: unknown) => {
          failed.push(
            `адрес ${kind === 'development' ? 'Development' : 'Production'} — ${reason(cause)}`,
          );
        });
      }
    } catch (cause) {
      failed.push(`адреса — ${reason(cause)}`);
    }
  }

  return failed;
}
